/**
 * Modal shown when the backend returns 412 session_mismatch, meaning the
 * browser cookie was swapped while this tab's React state still believed
 * it was the previous user (typically because another tab logged in as a
 * different account — cookies are origin-shared but React state isn't).
 *
 * Reload (not "try again") is the only resolution — refetching `/me` alone
 * doesn't rotate the cookie, and the user's React state is already stale
 * across the whole app, not just for the one failing call.
 *
 * The modal is intentionally undismissable: Radix ``AlertDialog`` already
 * suppresses click-outside by design (that's the difference from
 * ``Dialog``); we additionally suppress Escape, and the Reload button is
 * a plain ``Button`` (not the Radix ``AlertDialogAction``) so it doesn't
 * auto-close the dialog before ``location.reload()`` actually navigates.
 * If the reload is deferred (e.g., by a ``beforeunload`` prompt), the
 * modal stays in place rather than dropping into a closed-but-stale state.
 *
 * Background: SUP-3373.
 */

import { useSyncExternalStore } from 'react'

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import {
  getSessionChangedServerSnapshot,
  getSessionChangedSnapshot,
  subscribeSessionChanged,
} from '@/lib/session-changed-store'

function handleReload(): void {
  globalThis.location.reload()
}

function preventEscapeClose(event: KeyboardEvent): void {
  event.preventDefault()
}

export function SessionChangedModal(): React.JSX.Element {
  const open = useSyncExternalStore(
    subscribeSessionChanged,
    getSessionChangedSnapshot,
    getSessionChangedServerSnapshot,
  )

  return (
    <AlertDialog open={open}>
      <AlertDialogContent onEscapeKeyDown={preventEscapeClose}>
        <AlertDialogHeader>
          <AlertDialogTitle>Your session changed in another tab</AlertDialogTitle>
          <AlertDialogDescription>
            You appear to be signed in as a different account in another tab.
            To keep your data attributed to the correct user, reload this tab
            before continuing.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          {/*
            Plain Button — NOT AlertDialogAction — because AlertDialogAction
            auto-closes the dialog on click. If the reload is interrupted
            (e.g. a beforeunload prompt the user cancels), we want the modal
            to stay visible rather than vanish into a closed-but-stale state.
          */}
          <Button onClick={handleReload}>Reload</Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
